import { ISurvey } from '../classes/Survey';
import { IQuestion } from "./Question";
import { differenceInDays, differenceInMonths, differenceInYears } from 'date-fns';

// Función helper para truncar texto largo en etiquetas
const truncateLabel = (label: any, maxLength: number = 25): string => {
    if (!label) return '';
    const labelStr = String(label);
    return labelStr.length > maxLength ? labelStr.substring(0, maxLength) + '...' : labelStr;
};

interface IChartConfig {
    options: any,
    data: any,
}
/**
 * base class for charts, this uses the charts.js library
 * what this does is configure the options correctly, now it gives you some general options and 
    its child classes will give the specific options required for each type of graph
 * 
 */
export abstract class ChartConfig {
    protected chartConfig: IChartConfig = { options: {}, data: { labels: [], datasets: [{}, {}] } };
    protected question: IQuestion;
    protected valueIterator = 0;
    protected numberRandom = 0;
    constructor(question: IQuestion) {
        this.question = question;
        console.log("entra constructor question:",question);
        this.chartConfig.data.datasets=[{data:[]}];
    }

    /// basic options for everything
    protected options(): void {
        this.chartConfig.options = {
            responsive: true,
            plugins: {
                legend: {
                    position: 'top' as const,
                    display: false
                },
                title: {
                    display: true,
                },
            },
        };
    }

    protected labels_chartConfig(): void {
        console.log("call labels chartConfig")
        // Don't override if eNPS 1-10 scale is already configured
        if (this.chartConfig.data.labels && this.chartConfig.data.labels.length === 10) {
            console.log("Skipping label override - eNPS scale already configured");
            return;
        }
        //this.chartConfig.data.labels = ["label1", "label2", "label3", "label4"];
        this.chartConfig.data.labels = this.question.choiceQuestionProps?.choices;
    }

    /// test data only
    protected data_chartConfig(): void {
        this.chartConfig.data.datasets = [
            {
                ...this.chartConfig.data.datasets[0],
                //data: this.question.choiceQuestionProps?.choiceValues,
                //data: [1, 2, 3, 10],
                /*backgroundColor: [
                    'rgba(255, 99, 132, 0.2)',
                    'rgba(255, 159, 64, 0.2)',
                    'rgba(255, 205, 86, 0.2)',
                    'rgba(75, 192, 192, 0.2)'
                ],*/
            },
        ]
    }


    /**
     * This and the following function serve to simulate the operation of the rg between() function, which interpolates colors, this because this function is not enabled for tsx
     * @param hex 
     * @returns 
     */
    protected hexToRgb(hex: string) {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            red: parseInt(result[1], 16),
            green: parseInt(result[2], 16),
            blue: parseInt(result[3], 16)
        } : null;
    }
    protected interpolateColor(startRgb: string, endRgb: string | undefined, t: number): string {
        const start = this.hexToRgb(startRgb);
        const end = endRgb ? this.hexToRgb(endRgb) : this.hexToRgb(startRgb);
        if (!start || !end) {
            return startRgb;
        } else {
            
            const result = {
                r: Math.round(start.red + (end.red - start.red) * t),
                g: Math.round(start.green + (end.green - start.green) * t),
                b: Math.round(start.blue + (end.blue - start.blue) * t),
            };
            return `rgb(${result.r}, ${result.g}, ${result.b})`;    
        }
    }
    /**
     * This function is mainly for bars, what it does is that given the number of questions, 
     *   it generates colors with the interpolation function of interpolateColor
     *   this to generate the necessary number of colors for all questions
     */
    protected colors(): void {
        ///color management
        console.log("char data color: ",this.chartConfig);
        
        // Don't override if eNPS colors are already configured
        if (this.chartConfig.data.datasets[0]?.backgroundColor && 
            Array.isArray(this.chartConfig.data.datasets[0].backgroundColor) &&
            this.chartConfig.data.datasets[0].backgroundColor.length === 10) {
            console.log("Skipping color generation - eNPS colors already configured");
            return;
        }
        
        // Check if this question belongs to a template survey
        const templateSurveys = ["Employee Burnout", "Employee Engagement", "Job Satisfaction Survey", "eNPS"];
        const isTemplateSurvey = templateSurveys.includes(this.question.surveyTitle || "");
        
        // Para templates usamos el gradiente rojo-verde (emocional)
        // Para custom surveys usamos colores vibrantes Material Design (estilo Google Charts)
        let backgroundColors = isTemplateSurvey ? [
            `#FB5523`,  // Rojo (malo/negativo)
            `#FB744B`,
            `#FC9373`,
            `#FDB29B`,
            `#FDD835`,  // Amarillo (neutral)
            `#FFDE5C`,
            `#B1D798`,
            `#9BCC7B`,
            `#85C05D`,
            `#6DAE43`,  // Verde (bueno/positivo)
        ] : [
            `#4285F4`,  // Google Blue
            `#EA4335`,  // Google Red
            `#FBBC04`,  // Google Yellow
            `#34A853`,  // Google Green
            `#FF6D00`,  // Deep Orange
            `#9C27B0`,  // Purple
            `#00ACC1`,  // Cyan
            `#7CB342`,  // Light Green
            `#E91E63`,  // Pink
            `#FF9800`,  // Orange
        ];
        
        // For template surveys, reverse the colors: green = good (low agreement with negative statements)
        if (isTemplateSurvey) {
            backgroundColors = backgroundColors.reverse();
        }
        
        let borderColors = isTemplateSurvey ? [
            `#FB5523`,
            `#FB744B`,
            `#FC9373`,
            `#FDB29B`,
            `#FDD835`,
            `#FFDE5C`,
            `#B1D798`,
            `#9BCC7B`,
            `#85C05D`,
            `#6DAE43`,
        ] : [
            `#4285F4`,
            `#EA4335`,
            `#FBBC04`,
            `#34A853`,
            `#FF6D00`,
            `#9C27B0`,
            `#00ACC1`,
            `#7CB342`,
            `#E91E63`,
            `#FF9800`,
        ];
        
        let step = 1;
        if(this.chartConfig.data.datasets[0].data && this.chartConfig.data.datasets[0].data.length > 1) {
            step = 1 / (this.chartConfig.data.datasets[0].data.length - 1);
        }
        let newBackgroundColors = [];
        let newBorderColors = []

        for(let start = 0; start <= 1; start += step) {
            newBackgroundColors.push(this.interpolateColor(backgroundColors[0], backgroundColors[backgroundColors.length - 1], start));
            //newBorderColors.push(this.interpolateColor(borderColors[0], borderColors[borderColors.length - 1], start));
        }
        console.log("new backoug,",newBackgroundColors);
        this.chartConfig.data.datasets = [
            {
                ...this.chartConfig.data.datasets[0],
                //data: [1, 2, 3, 5],
                backgroundColor: newBackgroundColors,
            },
        ]
    }

    ///load the test dataset first
    protected others(): void {
        this.chartConfig.data.datasets = [
            {
                ...this.chartConfig.data.datasets[0],
                //label: 'Dataset 1',
            },
        ];
    }
    
    ///chart config setter and getter for its child classes
    public getChartConfig () : IChartConfig {
        return this.chartConfig;
    }
    public setChartConfig(value : IChartConfig) : void {
        this.chartConfig = value;
    }

    //This function is important, it is the one that calls and gives the basic default values of configurations
    public createChartConfigChart(): IChartConfig {
        this.labels_chartConfig();
        //this.data_chartConfig();
        this.others();
        this.options();
        this.colors();
        return this.chartConfig;
    }
}

export class RatingChartConfig extends ChartConfig {
    ///bar graph         console.log("bar graph questions: ")
    constructor(question: IQuestion) {
        super(question);

        const chartConfigs = this.createChartConfigChart();
        console.log("*** question:",question);
        console.log(" chartConfigs: ",chartConfigs);
        console.log("Soy bar graph: ",question.score);
        //chartConfigs.data.datasets[0].data.push( question.score );
        //this.data();
        //this.setChartConfig(chartConfigs);
    }
    public createChartConfig() : IChartConfig {
        console.log("createChartconfig nps?1");
        this.data();
        const chartConfigs = this.createChartConfigChart();

        // Check if this is a template survey
        const templateSurveys = ["Employee Burnout", "Employee Engagement", "Job Satisfaction Survey", "eNPS"];
        const isTemplateSurvey = templateSurveys.includes(this.question.surveyTitle || "");
        console.log("[ChartConfig] surveyTitle:", this.question.surveyTitle, "isTemplateSurvey:", isTemplateSurvey);

        chartConfigs.options = {
            //...chartConfigs.options,
            indexAxis: isTemplateSurvey ? 'y' : 'x',
            scales: {
                [isTemplateSurvey ? 'y' : 'x']: {
                    type: 'category',
                },
            },
            responsive: true,
            barThickness: 'flex',
            tooltips: {
                enabled: false,
            },
            /*responsive: true,
            maintainAspectRatio: false,

            aspectRatio: window.innerWidth / window.innerHeight, // agregar esta opción

            datasets: [{
            label: 'My First Dataset',
            borderWidth: 1,
            }]*/
        }
        console.log("antes:",chartConfigs);
        if( !this.isENPSSurvey()) {
            console.log("entra isENPSSurvey ");
            const templateSurveys = ["Employee Burnout", "Employee Engagement", "Job Satisfaction Survey", "eNPS"];
            const isTemplateSurvey = templateSurveys.includes(this.question.surveyTitle || "");
            
            // Responsive font sizes
            const isMobile = window.innerWidth < 768;
            const tickFontSize = isMobile ? 10 : 12;
            const maxLabelLength = isMobile ? 10 : 15;
            
            chartConfigs.options = {
                ...chartConfigs.options,
                indexAxis: isTemplateSurvey ? 'y' : 'x',
                responsive:true,
                maintainAspectRatio: false,
                scales: isTemplateSurvey ? {
                    x: {
                        stacked: false,
                        grid: {
                            display: true,    
                        },
                        ticks:{
                            display: true,
                            beginAtZero: true,
                            font: {
                                size: tickFontSize
                            }
                        },
                    },
                    y: {
                        stacked: false,
                        grid: {
                            display: true,
                        },
                        ticks: {
                            font: {
                                size: tickFontSize
                            },
                            callback: function(value: any) {
                                const label = (this as any).getLabelForValue(value);
                                return truncateLabel(label, maxLabelLength);
                            }
                        }
                    },
                } : {
                    y: {
                        stacked: false,
                        grid: {
                            display: true,    
                        },
                        ticks:{
                            display: true,
                            beginAtZero: true,
                            font: {
                                size: tickFontSize
                            }
                        },
                    },
                    x: {
                        stacked: false,
                        grid: {
                            display: true,
                        },
                        ticks: {
                            font: {
                                size: tickFontSize
                            },
                            callback: function(value: any) {
                                const label = (this as any).getLabelForValue(value);
                                return truncateLabel(label, maxLabelLength);
                            }
                        }
                    },
                },
                plugins: { ...chartConfigs.options.plugins,
                    tooltip: {
                        callbacks: {
                            title: () => '', // Oculta el label del eje X en el título
                        }
                    }
                },
                // plugins: [{
                //     title: {
                //         display: true,
                //     },
                //     legend: {
                //         display: false
                //     },
                //     datalabels: {
                //         labels: {
                //             label1: {
                //                 formatter: function(value:any, context:any) {
                //                     return Math.round((value/chartConfigs.data.datasets[0].data.reduce((a:any, b:any) => a + b, 0))*100) + '%';
                //                 }
                //             },
                //             label2: {
                //                 anchor: 'end',
                //                 align: 'end',
                //                 formatter: Math.round,
                //                 font: {
                //                     weight: 'medium',
                //                     size: '14px',
                //                     fontFamily: "'IBM Plex Sans', sans-serif",
                //                     color: '#1A237E'
                //                 }
                //             }
                //         },
                //     },
                // }, floatingLabels],
            }
            console.log("sale isENPSSurvey");
        }
        console.log("despues: ",chartConfigs)

        this.setChartConfig(chartConfigs);
        return chartConfigs;
    }
    data() {
        //const chartConfigs = this.createChartConfigChart();
        const chartConfigs = this.getChartConfig();
        
        // Special handling for eNPS: always show full 1-10 scale
        if (this.isENPSSurvey()) {
            console.log("*** eNPS detected, creating full 1-10 scale");
            console.log("choiceQuestionProps:", this.question.choiceQuestionProps);
            
            const fullScaleData = new Array(10).fill(0);
            const fullScaleLabels = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
            
            // Map the choice values to the correct positions
            // choiceValues[i] is the count for choice i, and choices[i] is the label
            if (this.question.choiceQuestionProps?.choiceValues && this.question.choiceQuestionProps?.choices) {
                console.log("Original choices:", this.question.choiceQuestionProps.choices);
                console.log("Original choiceValues:", this.question.choiceQuestionProps.choiceValues);
                
                this.question.choiceQuestionProps.choices.forEach((choice, index) => {
                    const choiceNumber = parseInt(choice);
                    console.log(`Processing choice[${index}]: "${choice}" -> number: ${choiceNumber}, value: ${this.question.choiceQuestionProps?.choiceValues[index]}`);
                    if (!isNaN(choiceNumber) && choiceNumber >= 1 && choiceNumber <= 10) {
                        fullScaleData[choiceNumber - 1] = this.question.choiceQuestionProps?.choiceValues[index] || 0;
                    }
                });
                
                console.log("Final fullScaleData:", fullScaleData);
            }
            
            // Generate interpolated colors for 1-10 scale
            const enpsColors = [];
            for (let score = 1; score <= 10; score++) {
                if (score <= 5) {
                    // Red to yellow (1-5)
                    const t = (score - 1) / 4;
                    enpsColors.push(this.interpolateColor("#FB5523", "#FDD835", t));
                } else {
                    // Yellow to green (6-10)
                    const t = (score - 5) / 5;
                    enpsColors.push(this.interpolateColor("#FDD835", "#6DAE43", t));
                }
            }
            
            console.log("eNPS colors:", enpsColors);
            
            chartConfigs.data.labels = fullScaleLabels;
            chartConfigs.data.datasets = [
                {
                    ...this.chartConfig.data.datasets[0],
                    data: fullScaleData,
                    backgroundColor: enpsColors,
                    borderRadius: 5,
                },
            ];
            
            this.setChartConfig(chartConfigs);
            // Don't call createChartConfigChart again for eNPS to avoid overwriting labels
            return;
        } else {
            chartConfigs.data.datasets = [
                {
                    ...this.chartConfig.data.datasets[0],
                    data: this.question.choiceQuestionProps?.choiceValues,
                    borderRadius: 5,
                },
            ];
        }
        
        this.setChartConfig(chartConfigs);
        const chartConfigs2 = this.createChartConfigChart();
        this.setChartConfig(chartConfigs2);
    }
    private isENPSSurvey():boolean {
        const questions = ["How likely is it that you would recommend this company to a friend or colleague?"];
        console.log("this.question.title: ",this.question.title);
        for(const valueQuestionsCurr of questions) {
            console.log("comparacion: ",valueQuestionsCurr === this.question.title )
            if(valueQuestionsCurr === this.question.title ) {
                return true;
            }
        }
        return false;
    }

}

export class TextChartConfig extends ChartConfig {
    ///donut
    constructor(question: IQuestion) {
        super(question);
        console.log("Soy donut graph");
    }
    labels() {
        this.chartConfig.data.labels = ["Negative", "Neutral", "Positive"];
    }
    public createChartConfig() : IChartConfig { 
        const chartConfigs = this.createChartConfigChart();
        chartConfigs.options.plugins.legend = {
           position: 'bottom', ///top
        }
        chartConfigs.options.plugins = {
            ...chartConfigs.options.plugins,
            tooltips: {
                enabled: false,
            },
            datalabels: {
                display: false,
            },
        }
        chartConfigs.options.layout = {
            padding: 0,
        };
        chartConfigs.options = {
            ...chartConfigs.options,
            maintainAspectRatio: false,
        }

        this.setChartConfig(chartConfigs);
        this.data_prop();
        return chartConfigs;
    }
    data_prop() {
        const chartConfigs = this.createChartConfigChart();
        const choiceValues: number[] = this.calculateChoiceValues();
        let backgroundColors = [
            '#FB5523',
            '#FDD835',
            '#6DAE43',      
            ];
        let borderColors = [
            '#FB5523',
            '#FDD835',
            '#6DAE43',
        ];

        chartConfigs.data.datasets = [
            {
                ...chartConfigs.data.datasets[0],
                data: choiceValues,
                //data: [1, 2, 3, 10],
                backgroundColor: backgroundColors,
                borderColor: borderColors,
            },
        ]
        this.setChartConfig(chartConfigs);
    }

    private calculateChoiceValues(): number[] {
        let choiceValues = [0, 0, 0];
        if (this.question.textQuestionProps != undefined) {
            for (const x of this.question.textQuestionProps?.sentimentScores) {
                if (x > 0.33) {
                    if (x > 0.66) choiceValues[2]++;
                    else choiceValues[1]++;
                }
                else choiceValues[0]++;
            }
        }
        return choiceValues;
    }
}

export class DimensionsChartConfig extends ChartConfig {
    constructor(
        //private questions: IQuestion[],
        //private filter: string,
        private allSurveys : any[],
        private currentSurvey: IQuestion[],
        private titleSurveyCurrent: string,
        private dimension: string,
        private filter: string,
        private currentSurveyId: string,
    ) {
        if(Object.keys(allSurveys).length === 0){allSurveys=[]};
        if(Object.keys(currentSurvey).length === 0){currentSurvey=[]};
        super({} as IQuestion);
    }
    public createChartConfig() : IChartConfig { 
        const currentSurveyObj = this.allSurveys.find(s => s.id === this.currentSurveyId);
        const dimensionObj = currentSurveyObj?.dimensions?.find((d: any) => d.name === this.dimension);
        const maxScore = dimensionObj?.maxScore || 5;

        const chartConfigs = this.createChartConfigChart();
        chartConfigs.options = {
            responsive: true,
            plugins: {
                title: {
                    display: true,
                },
                legend: {
                    display: false
                },
                datalabels: {
                    display: false  // Disable percentage labels
                },
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                },
                y: {
                    grid: {
                        display: false
                    },
                    beginAtZero: true,
                    min: 0,
                    max: maxScore + 1,  // Add 1 for better visualization
                    ticks: {
                        stepSize: 1,
                        min: 0,
                        max: maxScore + 1
                    }
                },
            },
            layout: {
                padding: 20
            },
            maintainAspectRatio: false,
            borderColor: '#1A237E',
            pointBackgroundColor: '#1A237E',
            pointRadius: 0,
        }
        this.setChartConfig(chartConfigs);
        this.labels();
        this.data();
        console.log("***line 3data chartConfigs.data.datasets: ",chartConfigs.data.datasets);
        return chartConfigs;
    }
    public labels() {
        const chartConfigs = this.getChartConfig();
        const now = new Date();
        const currentYear = now.getFullYear();
        
        switch (this.filter) {
            case "month":
                chartConfigs.data.labels = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                break;
            case "year":
                // Show last 5 years
                chartConfigs.data.labels = [
                    (currentYear - 4).toString(),
                    (currentYear - 3).toString(),
                    (currentYear - 2).toString(),
                    (currentYear - 1).toString(),
                    currentYear.toString()
                ];
                break;
            case "week":
                chartConfigs.data.labels = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
                break;
            default: chartConfigs.data.labels = [];
        }
        this.setChartConfig(chartConfigs);
    }

    data() {
        const chartConfigs = this.getChartConfig();
        const dimensionScores: number[] = this.calculateDimensionScoresOverTime();
        chartConfigs.data.datasets = [
            {
                ...chartConfigs.data.datasets[0],
                data: dimensionScores,
                backgroundColor: 'rgba(26, 35, 126, 0.1)',
                borderColor: '#1A237E',
                borderWidth: 2,
                fill: false,
                tension: 0.1,  // Lower tension for straighter lines (0 = completely straight)
                pointBackgroundColor: '#1A237E',
                pointRadius: 4,
                pointHoverRadius: 6,
            },
        ]
        this.setChartConfig(chartConfigs);
    }

    private calculateDimensionScoresOverTime(): number[] {
        switch( this.filter ) {
            case "week": return this.calculateDataLastWeek();
            case "month": return this.calculateDataLastMonth();
            case "year": return this.calculateDataLastYear();
        }
        console.log("[DimensionsChart] No matching filter, returning empty array");
        return [];
    }
    
    private calculateDataLastWeek(): number[] {
        const currentSurveyObj = this.allSurveys.find(s => s.id === this.currentSurveyId);
        if (!currentSurveyObj) {
            console.log('[DEBUG Week] No current survey found');
            return new Array(7).fill(0);
        }
        
        console.log('[DEBUG Week] Current survey object:', currentSurveyObj);
        
        // Use current date (today) to determine the current week range
        const currentSurveyDate = new Date();
        const currentDay = currentSurveyDate.getDay();
        
        const startOfWeek = new Date(currentSurveyDate);
        const diff = currentDay === 0 ? -6 : 1 - currentDay;
        startOfWeek.setDate(currentSurveyDate.getDate() + diff);
        startOfWeek.setHours(0, 0, 0, 0);
        
        const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        const dimensionScoresByDay: Map<string, number[]> = new Map();
        weekDays.forEach(day => dimensionScoresByDay.set(day, []));
        
        console.log('[DEBUG Week] Native responses:', currentSurveyObj.native_responses?.length || 0);
        console.log('[DEBUG Week] Dimension:', this.dimension);
        
        if (!currentSurveyObj.native_responses || currentSurveyObj.native_responses.length === 0) {
            console.log('[DEBUG Week] No native responses');
            return new Array(7).fill(0);
        }
        
        // Group responses by submission sessions (by date)
        const sessionsByDate = this.groupResponsesByDate(currentSurveyObj.native_responses);
        console.log('[DEBUG Week] Sessions by date:', sessionsByDate.size);
        
        // Calculate dimension score for each session
        sessionsByDate.forEach((responses, dateStr) => {
            console.log('[DEBUG Week] Processing session for date:', dateStr, 'with', responses.length, 'responses');
            // Parse date string and create Date with local timezone
            const [year, month, day] = dateStr.split('-').map(Number);
            const sessionDate = new Date(year, month - 1, day);
            console.log('[DEBUG Week] Session date:', sessionDate, 'Start of week:', startOfWeek, 'Current:', currentSurveyDate);
            
            if (sessionDate >= startOfWeek && sessionDate <= currentSurveyDate) {
                console.log('[DEBUG Week] Date is in range, calculating score...');
                const score = this.calculateDimensionScoreFromResponses(responses, currentSurveyObj);
                console.log('[DEBUG Week] Score result:', score);
                if (score !== null) {
                    const dayIndex = sessionDate.getDay();
                    const dayName = dayIndex === 0 ? 'Sunday' : weekDays[dayIndex - 1];
                    console.log('[DEBUG Week] Adding score to', dayName);
                    dimensionScoresByDay.get(dayName)?.push(score);
                }
            } else {
                console.log('[DEBUG Week] Date is NOT in range, skipping');
            }
        });
        
        // Average scores for each day
        const result = weekDays.map(day => {
            const scores = dimensionScoresByDay.get(day) || [];
            if (scores.length === 0) return 0;
            return scores.reduce((sum, score) => sum + score, 0) / scores.length;
        });
        
        return result;
    }
    
    private calculateDataLastMonth(): number[] {
        const currentSurveyObj = this.allSurveys.find(s => s.id === this.currentSurveyId);
        if (!currentSurveyObj) {
            return new Array(12).fill(0);
        }
        
        const currentSurveyDate = new Date(currentSurveyObj.createdAt || currentSurveyObj.updatedAt || currentSurveyObj.date);
        const currentYear = currentSurveyDate.getFullYear();
        
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
        const dimensionScoresByMonth: Map<string, number[]> = new Map();
        months.forEach(month => dimensionScoresByMonth.set(month, []));
        
        if (!currentSurveyObj.native_responses || currentSurveyObj.native_responses.length === 0) {
            return new Array(12).fill(0);
        }
        
        // Group responses by submission sessions
        const sessionsByDate = this.groupResponsesByDate(currentSurveyObj.native_responses);
        
        // Calculate dimension score for each session
        sessionsByDate.forEach((responses, dateStr) => {
            const sessionDate = new Date(dateStr);
            
            if (sessionDate.getFullYear() === currentYear && sessionDate <= currentSurveyDate) {
                const score = this.calculateDimensionScoreFromResponses(responses, currentSurveyObj);
                if (score !== null) {
                    const monthName = months[sessionDate.getMonth()];
                    dimensionScoresByMonth.get(monthName)?.push(score);
                }
            }
        });
        
        // Average scores for each month
        const result = months.map(month => {
            const scores = dimensionScoresByMonth.get(month) || [];
            if (scores.length === 0) return 0;
            return scores.reduce((sum, score) => sum + score, 0) / scores.length;
        });
        
        return result;
    }
    
    private calculateDataLastYear(): number[] {
        const currentSurveyObj = this.allSurveys.find(s => s.id === this.currentSurveyId);
        if (!currentSurveyObj) {
            return new Array(5).fill(0);
        }
        
        const currentSurveyDate = new Date(currentSurveyObj.createdAt || currentSurveyObj.updatedAt || currentSurveyObj.date);
        const currentYear = currentSurveyDate.getFullYear();
        
        const years = [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
        const dimensionScoresByYear: Map<number, number[]> = new Map();
        years.forEach(year => dimensionScoresByYear.set(year, []));
        
        if (!currentSurveyObj.native_responses || currentSurveyObj.native_responses.length === 0) {
            return new Array(5).fill(0);
        }
        
        // Group responses by submission sessions
        const sessionsByDate = this.groupResponsesByDate(currentSurveyObj.native_responses);
        
        // Calculate dimension score for each session
        sessionsByDate.forEach((responses, dateStr) => {
            const sessionDate = new Date(dateStr);
            const surveyYear = sessionDate.getFullYear();
            
            if (years.includes(surveyYear) && sessionDate <= currentSurveyDate) {
                const score = this.calculateDimensionScoreFromResponses(responses, currentSurveyObj);
                if (score !== null) {
                    dimensionScoresByYear.get(surveyYear)?.push(score);
                }
            }
        });
        
        // Average scores for each year
        const result = years.map(year => {
            const scores = dimensionScoresByYear.get(year) || [];
            if (scores.length === 0) return 0;
            return scores.reduce((sum, score) => sum + score, 0) / scores.length;
        });
        
        return result;
    }
    
    private groupResponsesByDate(responses: any[]): Map<string, any[]> {
        const sessionsByDate = new Map<string, any[]>();
        
        responses.forEach(response => {
            // Extract date (without time) from createdAt using LOCAL date
            const responseDate = new Date(response.createdAt);
            // Use local date instead of UTC to avoid timezone issues
            const year = responseDate.getFullYear();
            const month = String(responseDate.getMonth() + 1).padStart(2, '0');
            const day = String(responseDate.getDate()).padStart(2, '0');
            const dateKey = `${year}-${month}-${day}`;
            
            if (!sessionsByDate.has(dateKey)) {
                sessionsByDate.set(dateKey, []);
            }
            sessionsByDate.get(dateKey)?.push(response);
        });
        
        return sessionsByDate;
    }
    
    private calculateDimensionScoreFromResponses(responses: any[], survey: any): number | null {
        console.log('[DEBUG CalcScore] Starting calculation for', responses.length, 'responses');
        
        // Find the current dimension in the survey
        const dimension = survey.dimensions?.find((d: any) => d.name === this.dimension);
        if (!dimension) {
            console.log('[DEBUG CalcScore] Dimension not found:', this.dimension);
            return null;
        }
        console.log('[DEBUG CalcScore] Found dimension:', dimension);
        
        // Get question IDs that belong to this dimension
        const dimensionQuestionIds = this.getDimensionQuestionIds(survey, this.dimension);
        if (dimensionQuestionIds.length === 0) {
            console.log('[DEBUG CalcScore] No question IDs found for dimension');
            return null;
        }
        console.log('[DEBUG CalcScore] Dimension question IDs:', dimensionQuestionIds);
        
        // Filter responses for this dimension's questions
        const dimensionResponses = responses.filter((r: any) => 
            dimensionQuestionIds.includes(r.nativeQuestionId)
        );
        console.log('[DEBUG CalcScore] Filtered responses:', dimensionResponses.length, 'out of', responses.length);
        
        if (dimensionResponses.length === 0) {
            console.log('[DEBUG CalcScore] No responses for this dimension');
            return null;
        }
        
        // Get value mapping (content -> numeric value)
        const valueMap = this.obtainValuesResponse();
        console.log('[DEBUG CalcScore] Value map size:', valueMap.size);
        
        // Calculate average score
        let totalScore = 0;
        let validCount = 0;
        
        dimensionResponses.forEach((response: any) => {
            const numericValue = valueMap.get(response.content);
            if (numericValue !== undefined) {
                totalScore += numericValue;
                validCount++;
            }
        });
        
        if (validCount === 0) {
            console.log('[DEBUG CalcScore] No valid responses');
            return null;
        }
        
        const avgScore = totalScore / validCount;
        console.log('[DEBUG CalcScore] Final score:', avgScore, '(', validCount, 'valid responses)');
        return avgScore;
    }
    
    private getDimensionQuestionIds(survey: any, dimensionName: string): number[] {
        // Map dimension names to their question IDs
        if (!survey.questions) {
            console.log('[DEBUG] Survey has no questions array');
            return [];
        }
        
        // Convert display name back to original dimension name
        const originalDimensionName = this.getOriginalDimensionName(dimensionName);
        console.log('[DEBUG] Display name:', dimensionName, '-> Original:', originalDimensionName);
        
        const questionIds: number[] = [];
        survey.questions.forEach((q: any) => {
            // Match by dimension property using original name
            if (q.dimension === originalDimensionName) {
                const qId = parseInt(q.id);
                if (!isNaN(qId)) {
                    questionIds.push(qId);
                }
            }
        });
        
        console.log('[DEBUG] Question IDs for dimension', dimensionName, ':', questionIds);
        return questionIds;
    }
    
    private getOriginalDimensionName(displayName: string): string {
        // Reverse mapping of getDimensionDisplayName from SurveyConverter
        switch (displayName) {
            case "Employee Exhaustion":
                return "Exhaustion";
            case "Employee Engagement":
                return "Disengagement";
            default:
                return displayName;
        }
    }
    
    private obtainValuesResponse() {
        const myAns = new Map();
        const dataSurveyEmployeeEngagement = ["Never","Rarely","Sometimes","Often","Always"];
        const dataSurveyEmployeeBurnout = ["Strongly agree","Agree","Disagree","Strongly disagree"];
        const dataSurveyJobSatisfactionSurvey = ["Very dissatisfied","Dissatisfied","Slightly dissatisfied","Uncertain","Slightly Satisfied","Satisfied","Very Satisfied"];
        let valueNumber = 1;
        switch(this.titleSurveyCurrent) {
            case "Employee Engagement": 
                for(let value of dataSurveyEmployeeEngagement) {
                    myAns.set(value,valueNumber);
                    valueNumber++;
                }
                break;
            case "Employee Burnout": 
                for(let value of dataSurveyEmployeeBurnout) {
                    myAns.set(value,valueNumber);
                    valueNumber++;
                }
                break;
            case "Job Satisfaction Survey": 
                for(let value of dataSurveyJobSatisfactionSurvey) {
                    myAns.set(value,valueNumber);
                    valueNumber++;
                }
                break;
        }
        console.log("Map: ",myAns);
        console.log("this.titleSurveyCurrent: ",this.titleSurveyCurrent);
        return myAns;
    }

    private isValidQuestionDimensionCurr(value : number) {
        let iterator;
        ///this is for Employee Engagement
        const surveyEmployeeEngagement = ["Vigor","Dedication","Absorption"];
        const mapEmployeeEngagement = new Map();
        const numberQuestionSurveyEmployeeEngagement = [1,1,1];
        const cumulativeSumNumberQuestionsurveyEmployeeEngagement = [0];
        
        iterator = 0;
        for(let value of numberQuestionSurveyEmployeeEngagement) {
            cumulativeSumNumberQuestionsurveyEmployeeEngagement.push( cumulativeSumNumberQuestionsurveyEmployeeEngagement[ iterator ] + value);
            mapEmployeeEngagement.set(surveyEmployeeEngagement[iterator], iterator+1);
            iterator++;
        }
        cumulativeSumNumberQuestionsurveyEmployeeEngagement.push( cumulativeSumNumberQuestionsurveyEmployeeEngagement[ iterator ] + 1);
        const sumTotalNumberQuestionSurveyEmployeeEngagement = cumulativeSumNumberQuestionsurveyEmployeeEngagement[ iterator ];


        ///Employee Burnout
        const surveyEmployeeBurnout = ["Disengagement","Exhaustion"];
        const mapEmployeeBurnout = new Map();
        const numberQuestionSurveyEmployeeBurnout = [8,8];
        const cumulativeSumNumberQuestionsurveyEmployeeBurnout = [0];
        
        iterator = 0;
        for(let value of numberQuestionSurveyEmployeeBurnout) {
            cumulativeSumNumberQuestionsurveyEmployeeBurnout.push( cumulativeSumNumberQuestionsurveyEmployeeBurnout[ iterator ] + value);
            mapEmployeeBurnout.set(surveyEmployeeBurnout[iterator], iterator+1);
            iterator++;
        }
        cumulativeSumNumberQuestionsurveyEmployeeBurnout.push( cumulativeSumNumberQuestionsurveyEmployeeBurnout[ iterator ] + 1);
        const sumTotalNumberQuestionSurveyEmployeeBurnout = cumulativeSumNumberQuestionsurveyEmployeeBurnout[ iterator ];


        ///Job Satisfaction Survey
        const surveyJobSatisfactionSurvey = ["Job Security","Pay","Colleagues","Supervision","Growth Opportunities"];
        const mapJobSatisfactionSurvey = new Map();
        const numberQuestionSurveyJobSatisfactionSurvey = [3,2,4,3,2];
        const cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey  = [0];
        
        iterator = 0;
        for(let value of numberQuestionSurveyJobSatisfactionSurvey) {
            cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey.push( cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey[ iterator ] + value);
            mapJobSatisfactionSurvey.set(surveyJobSatisfactionSurvey[iterator], iterator+1);
            iterator++;
        }
        cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey.push( cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey[ iterator ] + 1);
        const sumTotalNumberQuestionSurveyJobSatisfactionSurvey = cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey[ iterator ];


        switch(this.titleSurveyCurrent) {
            case "Employee Engagement": 
                value = value % sumTotalNumberQuestionSurveyEmployeeEngagement;
                iterator = mapEmployeeEngagement.get( this.dimension );
                if(value > cumulativeSumNumberQuestionsurveyEmployeeEngagement[ iterator - 1 ] 
                    && value < cumulativeSumNumberQuestionsurveyEmployeeEngagement[ iterator + 1 ]) {
                        return  true;
                    } else { return false; }
            case "Employee Burnout":
                value = value % sumTotalNumberQuestionSurveyEmployeeBurnout;
                iterator = mapEmployeeBurnout.get( this.dimension );
                if(value > cumulativeSumNumberQuestionsurveyEmployeeBurnout[ iterator - 1 ] 
                    && value < cumulativeSumNumberQuestionsurveyEmployeeBurnout[ iterator + 1 ]) {
                        return  true;
                    } else { return false; }
            case "Job Satisfaction Survey":
                value = value % sumTotalNumberQuestionSurveyJobSatisfactionSurvey;
                iterator = mapJobSatisfactionSurvey.get( this.dimension );
                if(value > cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey[ iterator - 1 ] 
                    && value < cumulativeSumNumberQuestionsurveyJobSatisfactionSurvey[ iterator + 1 ]) {
                        return  true;
                    } else { return false; }
        }
        
    }
    private extractDateParts(isoDateString: string): { year: number, month: number, day: number } {
        const date = new Date(isoDateString);
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1; // Sumamos 1 porque los meses empiezan en 0
        const day = date.getUTCDate();
        return { year, month, day };
    }

    private getDaysDifference(day: number, month: number, year: number): number {
        const currentDate = new Date();
        const targetDate = new Date(year, month - 1, day);
        const dayDiff = differenceInDays(currentDate, targetDate);
        if(dayDiff < 0) {
            return -dayDiff;
        }
        return dayDiff;
    }

}

const floatingLabels = {
    id: 'floatingLabels',
    afterDatasetsDraw(chart: any, args: any, options: any) {
        const { ctx, chartArea: { top }, scales: { x, y}} = chart;

        for(let i = 0; i < chart.config.data.datasets[0].data.length; i++){

            const xPosition = chart.getDatasetMeta(1).data[i].x;
            const yPosition = top + 6;

            ctx.save();

            if(chart.isDatasetVisible(1) === true) {
                ctx.fillStyle = 'rgba(255, 26, 104, 1)';
            } else {
                ctx.fillStyle = 'transparent';
            }

            ctx.textAlign = 'center';
            ctx.font = 'bolder 12px Arial';
            ctx.fillText(chart.config.data.datasets[0].data[i], xPosition, yPosition);
        }
    }
}